import { useToggleModal } from "@customer-app/state/actionCreators/modal";
import { useProductsListForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";
import { Fade, Link, Typography } from "@mui/material";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { OrgUtils } from "@smartrr/shared/entities/Organization";
import {
  IPurchaseState,
  IUnavailableVariantsAndDeliveries,
  PurchaseStateAndDelivery,
} from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import {
  ScheduledDeliveryGroupWithPurchSt,
  getPurchStFutureScheduledDeliveriesWithNonActive,
} from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { getActiveAndPausedCustomerPurchaseStates } from "@smartrr/shared/utils/getActiveAndPausedCustomerPurchaseStates";
import { rgba } from "polished";
import React, { useCallback } from "react";
import { Trans } from "react-i18next";

import { useUnavailableItemBannerCopy, useUnavailableLineItems } from "./utils";
import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";
import { useOrganization } from "../../../utils";
import { AquariusBanner } from "../../AquariusBanner";
import { usePurchasablesForSubscribing } from "../../hooks/usePurchasablesForSubscribing";
import { usePurchaseStates } from "../../hooks/usePurchaseStates";
import { useUnavailableVariantsAndDeliveries } from "../../hooks/useUnavailableVariantsAndDeliveries";
import { useSubscriptionActionStates, useSubscriptionActions } from "../../store/SubscriptionActionStore";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const ManageOutOfStockAndUnavailable = ({
  targetDelivery,
}: {
  targetDelivery?: PurchaseStateAndDelivery;
}) => {
  const { unavailableVariantsAndDeliveries } = useUnavailableVariantsAndDeliveries();
  const purchaseStates = getActiveAndPausedCustomerPurchaseStates(usePurchaseStates());
  const organization = useOrganization();

  const showAllPurchaseStates = !targetDelivery;

  const filteredPurchaseStates = purchaseStates.filter(cps => {
    if (showAllPurchaseStates) {
      return cps;
    }
    return cps.id === targetDelivery.purchaseState.id;
  });

  const banners = filteredPurchaseStates.map(cps => {
    const scheduledDeliveries = getPurchStFutureScheduledDeliveriesWithNonActive(
      cps,
      OrgUtils.getBillingSchedule(organization),
      1
    );

    const delivery = scheduledDeliveries?.length ? scheduledDeliveries[0] : undefined;

    if (delivery) {
      return (
        <OutOfStockAndUnavailableCPS
          key={cps.id}
          delivery={delivery}
          unavailableVariantsAndDeliveries={unavailableVariantsAndDeliveries}
        />
      );
    }
    return <React.Fragment key={cps.id} />;
  });

  return <React.Fragment>{banners}</React.Fragment>;
};

interface OutOfStockAndUnavailableCPSProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  unavailableVariantsAndDeliveries: IUnavailableVariantsAndDeliveries;
}

function OutOfStockAndUnavailableCPS({
  delivery,
  unavailableVariantsAndDeliveries,
}: OutOfStockAndUnavailableCPSProps) {
  const toggleModal = useToggleModal();
  const deliveryScheduledIndex = delivery.indexFromScheduleStart;
  const allPurchasablesForUpcoming = useProductsListForSellingPlanId(delivery.purchaseState.sellingPlanId, false);

  const unavailableLineItems = useUnavailableLineItems(
    unavailableVariantsAndDeliveries,
    delivery.purchaseState.stLineItems,
    deliveryScheduledIndex
  );

  const purchasablesForSubscribing = usePurchasablesForSubscribing(delivery);

  const purchasableForUpcoming = purchasablesForSubscribing(delivery, allPurchasablesForUpcoming);

  const openSwapViewHandler = useCallback(
    (liId: string) =>
      toggleModal("MANAGE_SUBSCRIPTIONS", {
        deliveryId: getDeliveryId(delivery),
        generatePurchasables: true,
        initialLineItemId: liId,
        initialView: "Swap item",
        purchasablesForAdding: purchasableForUpcoming,
      }),
    [
      unavailableVariantsAndDeliveries.deliveries,
      purchasableForUpcoming,
      toggleModal,
      delivery.purchaseState.stLineItems,
    ]
  );

  if (!delivery) {
    return <React.Fragment />;
  }
  return (
    <React.Fragment>
      {unavailableLineItems.map(li => (
        <OutOfStockBanner li={li} onSwap={openSwapViewHandler} delivery={delivery} key={li.id} />
      ))}
    </React.Fragment>
  );
}

function OutOfStockBanner({
  li,
  onSwap,
  delivery,
}: {
  li: IPurchaseStateLineItem;
  onSwap: (liId: string) => void;
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
}) {
  const { onRemove, setDelivery } = useSubscriptionActions();
  const { isLoading } = useSubscriptionActionStates();
  const bannerCopy = useUnavailableItemBannerCopy(li, delivery);
  const theme = useSmartrrTheme();

  const onRemoveLineItem = useCallback(async () => {
    setDelivery(delivery);
    await onRemove(li.id);
    return;
  }, [li.id]);

  if (bannerCopy === "") {
    return <React.Fragment />;
  }

  return (
    <Fade in>
      <div>
        <AquariusBanner state="warning" useIcon={!isLoading}>
          {isLoading ? (
            <DotSpinner size={18} />
          ) : (
            <Typography variant="body1" data-testid="oos-banner-copy">
              <Trans
                defaults={bannerCopy}
                components={[
                  <Link
                    data-testid="oos-banner-copy-swap"
                    component="span"
                    underline="always"
                    sx={{
                      color: theme.modernBodyFontColor,
                      cursor: "pointer",
                      textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                    }}
                    onClick={() => onSwap(li.id)}
                    key={0}
                  />,
                  <Link
                    data-testid="oos-banner-copy-remove"
                    component="span"
                    underline="always"
                    sx={{
                      color: theme.modernBodyFontColor,
                      cursor: "pointer",
                      textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                    }}
                    onClick={onRemoveLineItem}
                    key={1}
                  />,
                ]}
              />
            </Typography>
          )}
        </AquariusBanner>
      </div>
    </Fade>
  );
}
